import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CustomToastService, ToastType, ToastAction } from "../../shared/service/toast.service";
import { NotificationMobileToastService } from '../../shared/components/notification-mobile-toast/notification-mobile-toast.service';

export interface Notification {
  message: {
    id: number;
    message: string;
    notification_type: string;
    read: boolean;
    title: string;
    date: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  public readonly WEBSOCKET_URL = environment.notificationsWebSocketUrl;
  public host = environment.apiUrlHost;
  private websocket?: WebSocket;
  /** When true, onclose must not schedule reconnect (e.g. logout). */
  private suppressWsReconnect = false;
  private wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private wsReconnectAttempts = 0;
  private readonly wsMaxReconnectAttempts = 8;
  private readonly wsBaseReconnectDelayMs = 5000;
  private notifications = new BehaviorSubject<Notification[]>([]);
  private unreadCount = new BehaviorSubject<number>(0);
  private wsConnected = new BehaviorSubject<boolean>(false);
  /** Emits when the navbar (e.g. app-nav-right) should refresh its notification list. */
  private refreshRequested$ = new Subject<void>();
  /** Notifications created on the frontend (e.g. basket reopened) so they appear even if backend POST/GET lag. */
  private pendingNotifications: any[] = [];
  /** Prevents duplicate WebSocket + HTTP init when login and checkAuthStatus both run. Cleared on disconnect. */
  private notificationSessionActive = false;
  /** Tracks notifications already shown / known so the mobile top banner only fires for new items. */
  private readonly seenNotificationKeys = new Set<string>();
  private mobileBannerBaselineSeeded = false;
  private mobileBannerPollTimer: ReturnType<typeof setTimeout> | null = null;
  private mobileBannerPollGeneration = 0;
  /** HTTP fallback when WebSocket is unavailable (common on localhost). */
  private mobileBannerHttpPollTimer: ReturnType<typeof setInterval> | null = null;
  /** Safety-net only — actions use immediate banner + fast HTTP poll. */
  private readonly mobileBannerHttpPollMs = 8_000;
  private readonly mobileBannerLogPrefix = '[MobileTopBanner]';

  constructor(
    private http: HttpClient,
    private toastService: CustomToastService,
    private mobileToastService: NotificationMobileToastService,
    private ngZone: NgZone
  ) {
    // Don't initialize WebSocket on service initialization
    // Wait for login/authentication instead
  }

  /**
   * Initialize the notification system after user login (or restored session).
   * Safe to call from multiple places; runs once per session until disconnect().
   */
  public initializeNotificationSystem() {
    if (this.notificationSessionActive) {
      return;
    }
    this.notificationSessionActive = true;
    console.log('Initializing notification system after login');
    this.initializeWebSocket();
    this.loadNotifications();
    this.seedMobileBannerBaseline();
  }

  public initializeWebSocket() {
    if (environment.notificationsWebSocketEnabled === false) {
      console.warn(
        '[Notifications] WebSocket disabled in environment; using HTTP APIs only. ' +
          'Enable notificationsWebSocketEnabled when wss://…/ws/notifications/ is available.'
      );
      return;
    }

    this.suppressWsReconnect = false;
    this.detachAndCloseExistingSocket();

    console.log('Initializing WebSocket...');

    /**
     * Cookie-based auth (same as REST with withCredentials: true):
     * - Login must Set-Cookie for the API host (e.g. Domain=.dev.zumport.com so the browser
     *   also sends it on wss://api.dev.zumport.com/...).
     * - There is no WebSocket API to attach Authorization or "withCredentials"; the browser
     *   includes matching cookies on the upgrade request automatically.
     * - Do not put JWTs in the URL for this flow; verify in DevTools → Network → WS → Headers → Cookie.
     */
    const websocketUrl = this.buildNotificationsWebSocketUrl();

    this.websocket = new WebSocket(websocketUrl);

    this.websocket.onopen = () => {
      this.wsReconnectAttempts = 0;
      console.log('WebSocket connection established');
      this.wsConnected.next(true);
      this.stopBackgroundHttpPoll();
    };

    this.websocket.onmessage = (event) => {
      this.ngZone.run(() => {
        try {
          const parsed = JSON.parse(event.data);
          if (!this.isActionableWebSocketNotification(parsed)) {
            return;
          }
          const notification = this.normalizeWebSocketNotification(parsed);
          console.warn(`${this.mobileBannerLogPrefix} WebSocket notification received`, {
            title: notification?.message?.title,
            type: notification?.message?.notification_type
          });
          this.notifications.next([notification, ...this.notifications.value]);
          this.updateUnreadCount();
          this.showToastForNotification(notification);
        } catch (error) {
          console.error('[Notifications] Invalid WebSocket payload:', error);
        }
      });
    };

    this.websocket.onclose = () => {
      this.wsConnected.next(false);
      this.startBackgroundHttpPoll();
      if (this.suppressWsReconnect) {
        return;
      }
      console.log('WebSocket connection closed.');
      this.scheduleWsReconnect();
    };

    this.websocket.onerror = () => {
      // Browsers only expose a generic Event; open DevTools → Network → WS → Headers.
      console.warn(
        '[Notifications] WebSocket error (handshake failed or connection dropped). Check: proxy Upgrade/WebSocket; WSS on /ws/notifications/. For cookie auth, the WS request must include the same session cookie as REST—if Cookie is missing, fix Set-Cookie Domain (e.g. .dev.zumport.com) on the API.'
      );
      this.wsConnected.next(false);
      this.startBackgroundHttpPoll();
    };
  }

  private detachAndCloseExistingSocket(): void {
    if (!this.websocket) {
      return;
    }
    const prev = this.websocket;
    prev.onopen = null;
    prev.onmessage = null;
    prev.onerror = null;
    prev.onclose = null;
    try {
      prev.close();
    } catch {
      /* ignore */
    }
    this.websocket = undefined;
  }

  /** Normalizes env URL to a single trailing slash; no auth query params (cookies carry the session). */
  private buildNotificationsWebSocketUrl(): string {
    return this.WEBSOCKET_URL.trim().replace(/\/*$/, '/');
  }

  private clearWsReconnectTimer(): void {
    if (this.wsReconnectTimer != null) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
  }

  private scheduleWsReconnect(): void {
    if (environment.notificationsWebSocketEnabled === false) {
      return;
    }
    if (this.wsReconnectAttempts >= this.wsMaxReconnectAttempts) {
      console.warn(
        `[Notifications] Stopped WebSocket reconnect after ${this.wsMaxReconnectAttempts} attempts. ` +
          'Fix the WSS endpoint or set notificationsWebSocketEnabled: false in environment, then reload.'
      );
      return;
    }
    this.clearWsReconnectTimer();
    const delay = Math.min(
      60_000,
      this.wsBaseReconnectDelayMs * Math.pow(2, this.wsReconnectAttempts)
    );
    this.wsReconnectAttempts += 1;
    console.log(`[Notifications] Reconnecting WebSocket in ${delay / 1000}s (attempt ${this.wsReconnectAttempts}/${this.wsMaxReconnectAttempts})…`);
    this.wsReconnectTimer = setTimeout(() => {
      this.wsReconnectTimer = null;
      this.initializeWebSocket();
    }, delay);
  }

  /** Ignore WebSocket keep-alives so they do not refresh the dropdown list. */
  private isActionableWebSocketNotification(raw: any): boolean {
    if (raw == null || typeof raw !== 'object') {
      return false;
    }
    const kind = String(raw.type ?? raw.event ?? raw.action ?? '').toLowerCase();
    if (kind && /^(ping|pong|heartbeat|ack|connected|ready|keepalive)$/.test(kind)) {
      return false;
    }

    const msg = raw.message;
    const title = (msg && typeof msg === 'object' ? msg.title : null) ?? raw.title;
    const body = typeof msg === 'string'
      ? msg
      : (msg && typeof msg === 'object' ? (msg.message ?? msg.description) : raw.message);
    const id = (msg && typeof msg === 'object' ? msg.id : null) ?? raw.id;
    return id != null || !!(title && String(title).trim()) || (typeof body === 'string' && !!body.trim());
  }

  /** Backend may send { message: {...} } or a flat notification object. */
  private normalizeWebSocketNotification(raw: any): Notification {
    const msg = raw?.message;
    if (msg && typeof msg === 'object' && !Array.isArray(msg)) {
      return {
        message: {
          id: msg.id ?? raw.id,
          title: msg.title ?? raw.title ?? '',
          message: typeof msg.message === 'string' ? msg.message : (raw.message ?? msg.description ?? ''),
          notification_type: msg.notification_type ?? raw.notification_type ?? 'info',
          read: msg.read ?? raw.read ?? false,
          date: msg.date ?? msg.created_at ?? raw.created_at ?? raw.date ?? new Date().toISOString()
        }
      };
    }

    return {
      message: {
        id: raw?.id,
        title: raw?.title ?? '',
        message: typeof raw?.message === 'string' ? raw.message : (raw?.description ?? raw?.body ?? ''),
        notification_type: raw?.notification_type ?? 'info',
        read: raw?.read ?? false,
        date: raw?.date ?? raw?.created_at ?? new Date().toISOString()
      }
    };
  }

  /**
   * Show toast notification based on notification type
   */
  private showToastForNotification(notification: Notification) {
    if (this.mobileToastService.isMobileViewport()) {
      console.warn(`${this.mobileBannerLogPrefix} showToastForNotification → mobile banner path`, {
        title: notification?.message?.title
      });
      this.markNotificationSeenForBanner(notification);
      this.mobileToastService.showFromNotification(notification);
      this.refreshRequested$.next();
      return;
    }

    const { title, message, notification_type } = notification.message;

    switch (notification_type) {
      case 'salesOrder':
        this.showSalesOrderToast(title, message, notification);
        break;
      case 'info':
        this.toastService.showInfo(message, title);
        break;
      case 'success':
        this.toastService.showSuccess(message, title);
        break;
      case 'warning':
        this.toastService.showWarning(message, title);
        break;
      case 'error':
        this.toastService.showError(message, title);
        break;
      case 'login':
        this.toastService.showSuccess(message, title || 'Login Successful');
        break;
      case 'password reset':
        this.toastService.showInfo(message, title || 'Password Reset');
        break;
      default:
        this.toastService.showInfo(message, title);
    }
  }

  /**
   * Show specialized toast for sales order notifications
   */
  private showSalesOrderToast(title: string, message: string, notification: Notification) {
    const checkNowAction: ToastAction = {
      text: 'Check now',
      onClick: () => {
        // Navigate to sales order or perform action
        this.handleSalesOrderAction(notification);
      },
      cssClass: 'toast-action-btn btn-primary'
    };

    // Show warning toast with action button for sales orders
    this.toastService.showWarningWithAction(
      message,
      title || 'backorder', // Default title as shown in image
      checkNowAction,
      {
        timeOut: 0, // Don't auto-dismiss
        closeButton: true,
        enableHtml: true,
        progressBar: false,
        toastClass: 'ngx-toastr toast-warning sales-order-toast'
      }
    );
  }

  /** Mobile top banner tap — mark read and run type-specific action. */
  handleMobileBannerTap(notification: Notification): void {
    this.handleMobileToastTap(notification);
  }

  handleMobileToastTap(notification: Notification): void {
    const type = notification.message?.notification_type;
    if (type === 'salesOrder') {
      this.handleSalesOrderAction(notification);
      return;
    }

    const id = notification.message?.id;
    if (id == null) {
      return;
    }

    this.markAsRead(id).subscribe({
      next: () => {
        const updatedNotifications = this.notifications.value.map(n =>
          n.message.id === id ? { ...n, message: { ...n.message, read: true } } : n
        );
        this.notifications.next(updatedNotifications);
        this.updateUnreadCount();
        this.requestNotificationRefresh();
      }
    });
  }

  /**
   * Handle sales order action when "Check now" is clicked
   */
  private handleSalesOrderAction(notification: Notification) {
    // Mark notification as read
    this.markAsRead(notification.message.id).subscribe({
      next: () => {
        console.log('Notification marked as read');
        // Update local state
        const updatedNotifications = this.notifications.value.map(n =>
          n.message.id === notification.message.id
            ? { ...n, message: { ...n.message, read: true } }
            : n
        );
        this.notifications.next(updatedNotifications);
        this.updateUnreadCount();
      },
      error: (error) => {
        console.error('Error marking notification as read:', error);
      }
    });

    // Add your navigation logic here
    // For example: this.router.navigate(['/sales-orders', orderId]);
    // Or open a modal, etc.
    console.log('Navigating to sales order details...');
  }

  /**
   * Clean up WebSocket connection on logout
   */
  public disconnect() {
    this.notificationSessionActive = false;
    this.clearWsReconnectTimer();
    this.wsReconnectAttempts = 0;
    if (this.websocket) {
      console.log('Closing WebSocket connection on logout');
      this.suppressWsReconnect = true;
      this.websocket.close();
    }
    this.websocket = undefined;
    this.wsConnected.next(false);
    this.notifications.next([]);
    this.unreadCount.next(0);
    this.seenNotificationKeys.clear();
    this.mobileBannerBaselineSeeded = false;
    this.clearMobileBannerPollTimer();
    this.stopBackgroundHttpPoll();
  }

  /** Backend: GET ${apiUrl}/AD/notifications/ */
  public loadNotifications() {
    this.http.get<Notification[]>(`${this.host}/AD/notifications/`)
      .subscribe({
        next: (notifications) => {
          this.notifications.next(notifications);
          this.updateUnreadCount();
        },
        error: (error) => {
          console.error('Error loading notifications:', error);
        }
      });
  }

  public updateUnreadCount() {
    const count = this.notifications.value.filter(n => this.isStoredNotificationUnread(n)).length;
    this.unreadCount.next(count);
  }

  /**
   * Same unread semantics as navbar / normalizeForDisplay: supports read|is_read on message object
   * or on the row, and avoids treating every item as unread when message is a string (no .read).
   */
  private isStoredNotificationUnread(n: any): boolean {
    if (!n) {
      return false;
    }
    const msg = n.message;
    if (msg && typeof msg === 'object' && !Array.isArray(msg)) {
      const read = msg.read ?? msg.is_read;
      if (read === true || read === 'true') {
        return false;
      }
      if (read === false || read === 'false') {
        return true;
      }
    }
    const read = n.read ?? n.is_read;
    return read !== true && read !== 'true';
  }

  // Set the notifications array and update unread count
  setNotifications(notifications: Notification[]) {
    this.notifications.next(notifications);
    this.updateUnreadCount(); // After setting notifications, update the unread count
  }

  /** Latest WebSocket-delivered notifications (for merging into navbar popup). */
  getNotificationsSnapshot(): Notification[] {
    return this.notifications.value;
  }

  getNotifications(): Observable<Notification[]> {
    return this.notifications.asObservable();
  }

  getUnreadCount(): Observable<number> {
    return this.unreadCount.asObservable();
  }

  /** Update the unread count (e.g. from navbar after loading unread list). Drives the badge on the bell icon. */
  setUnreadCount(count: number): void {
    this.unreadCount.next(count);
  }

  /** Call after an action that may create new notifications (e.g. reopen basket) so the navbar refreshes. */
  requestNotificationRefresh(): void {
    console.warn(`${this.mobileBannerLogPrefix} requestNotificationRefresh — starting HTTP poll`);
    this.refreshRequested$.next();
    this.pollForNewMobileBanner(0);
  }

  /** Record current notifications so existing items do not trigger the mobile top banner on login. */
  private seedMobileBannerBaseline(): void {
    console.warn(`${this.mobileBannerLogPrefix} seedMobileBannerBaseline — loading existing notifications`);
    this.getNotif().subscribe({
      next: (res) => {
        const items = this.mapNotificationList(res);
        items.forEach(item => this.markNotificationSeenForBanner(item));
        this.mobileBannerBaselineSeeded = true;
        console.warn(`${this.mobileBannerLogPrefix} baseline seeded`, {
          count: items.length,
          note: 'Existing notifications will NOT trigger the auto banner — only new ones after login.'
        });
        this.startBackgroundHttpPoll();
      },
      error: () => {
        this.mobileBannerBaselineSeeded = true;
        console.warn(`${this.mobileBannerLogPrefix} baseline seed failed — banner may show old items once`);
        this.startBackgroundHttpPoll();
      }
    });
  }

  /**
   * Poll HTTP periodically when WebSocket is down so new notifications still trigger the mobile banner.
   */
  private startBackgroundHttpPoll(): void {
    if (this.mobileBannerHttpPollTimer != null || !this.notificationSessionActive) {
      return;
    }
    if (environment.notificationsWebSocketEnabled !== false && this.wsConnected.value) {
      return;
    }

    console.warn(`${this.mobileBannerLogPrefix} starting HTTP poll fallback (WebSocket unavailable, safety-net every ${this.mobileBannerHttpPollMs / 1000}s)`);
    this.ngZone.run(() => this.checkForNewMobileBanner('http-poll-immediate'));
    this.mobileBannerHttpPollTimer = setInterval(
      () => this.ngZone.run(() => this.checkForNewMobileBanner('http-poll')),
      this.mobileBannerHttpPollMs
    );
  }

  private stopBackgroundHttpPoll(): void {
    if (this.mobileBannerHttpPollTimer != null) {
      clearInterval(this.mobileBannerHttpPollTimer);
      this.mobileBannerHttpPollTimer = null;
      console.warn(`${this.mobileBannerLogPrefix} stopped HTTP poll fallback (WebSocket connected)`);
    }
  }

  /** Shared check used by action-triggered poll and background HTTP poll. */
  private checkForNewMobileBanner(source: string): boolean {
    if (!this.mobileBannerBaselineSeeded) {
      return false;
    }
    if (!this.mobileToastService.isMobileViewport()) {
      return false;
    }

    this.getNotif().subscribe({
      next: (res) => {
        const items = this.mapNotificationList(res);
        const unseenUnread = items.filter(item => this.isDisplayNotificationUnread(item)
          && !this.isNotificationSeenForBanner(item));
        const latest = this.pickLatestNotification(unseenUnread);

        if (!latest) {
          return;
        }

        unseenUnread.forEach(item => this.markNotificationSeenForBanner(item));
        console.warn(`${this.mobileBannerLogPrefix} ${source} — new notification, showing banner`, {
          title: latest?.title ?? latest?.message?.title ?? null
        });
        this.mobileToastService.showFromNotification(this.toMobileBannerNotification(latest));
        this.refreshRequested$.next();
      }
    });

    return true;
  }

  /**
   * Poll HTTP notifications after actions like basket reopen (backend may lag vs WebSocket).
   * Shows the mobile top banner for the newest unread item not seen before.
   */
  private pollForNewMobileBanner(attempt: number): void {
    if (!this.mobileToastService.isMobileViewport()) {
      this.mobileToastService.logViewportStatus('pollForNewMobileBanner skipped — not mobile viewport');
      return;
    }

    if (attempt === 0) {
      this.clearMobileBannerPollTimer();
      this.mobileBannerPollGeneration += 1;
    }

    const pollGeneration = this.mobileBannerPollGeneration;
    console.warn(`${this.mobileBannerLogPrefix} pollForNewMobileBanner attempt ${attempt}`);

    this.getNotif().subscribe({
      next: (res) => {
        if (pollGeneration !== this.mobileBannerPollGeneration) {
          console.warn(`${this.mobileBannerLogPrefix} poll cancelled — newer poll started`);
          return;
        }

        const items = this.mapNotificationList(res);

        if (!this.mobileBannerBaselineSeeded) {
          items.forEach(item => this.markNotificationSeenForBanner(item));
          this.mobileBannerBaselineSeeded = true;
          console.warn(`${this.mobileBannerLogPrefix} poll seeded baseline`, { count: items.length });
          return;
        }

        const unseenUnread = items.filter(item => this.isDisplayNotificationUnread(item)
          && !this.isNotificationSeenForBanner(item));
        const latest = this.pickLatestNotification(unseenUnread);

        console.warn(`${this.mobileBannerLogPrefix} poll result`, {
          attempt,
          totalItems: items.length,
          unseenUnread: unseenUnread.length,
          latestTitle: latest?.title ?? latest?.message?.title ?? null
        });

        if (latest) {
          unseenUnread.forEach(item => this.markNotificationSeenForBanner(item));
          console.warn(`${this.mobileBannerLogPrefix} poll found new notification — triggering banner`);
          this.mobileToastService.showFromNotification(this.toMobileBannerNotification(latest));
          this.clearMobileBannerPollTimer();
          return;
        }

        if (attempt < 5) {
          const retryMs = 400;
          console.warn(`${this.mobileBannerLogPrefix} no new notification yet — retry in ${retryMs}ms`);
          this.clearMobileBannerPollTimer();
          this.mobileBannerPollTimer = setTimeout(
            () => this.ngZone.run(() => this.pollForNewMobileBanner(attempt + 1)),
            retryMs
          );
        } else {
          console.warn(`${this.mobileBannerLogPrefix} poll finished — no new notification after 6 attempts`);
        }
      }
    });
  }

  /** Newest notification first (by created_at / date). */
  private pickLatestNotification(items: any[]): any | null {
    if (!items?.length) {
      return null;
    }

    return [...items].sort((a, b) => this.notificationSortTime(b) - this.notificationSortTime(a))[0];
  }

  private notificationSortTime(item: any): number {
    const value = item?.created_at ?? item?.date ?? item?.message?.date;
    if (!value) {
      return 0;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private mapNotificationList(res: any): any[] {
    const rawList = Array.isArray(res) ? res : (res && res.results) ? res.results : [];
    return rawList.map((n: any) => this.normalizeForDisplay(n));
  }

  private notificationBannerKey(item: any): string | null {
    if (!item) {
      return null;
    }
    const id = item?.id ?? item?.message?.id;
    if (id != null) {
      return `id:${id}`;
    }
    const title = item?.title ?? item?.message?.title ?? '';
    const body = typeof item?.message === 'string'
      ? item.message
      : (item?.message?.message ?? item?.message ?? '');
    const date = item?.created_at ?? item?.message?.date ?? item?.date ?? '';
    if (!title && !body) {
      return null;
    }
    return `tmp:${title}:${body}:${date}`;
  }

  private markNotificationSeenForBanner(item: any): void {
    const key = this.notificationBannerKey(item);
    if (key) {
      this.seenNotificationKeys.add(key);
    }
  }

  private isNotificationSeenForBanner(item: any): boolean {
    const key = this.notificationBannerKey(item);
    return key != null && this.seenNotificationKeys.has(key);
  }

  private isDisplayNotificationUnread(item: any): boolean {
    const read = item?.read ?? item?.is_read;
    return read !== true && read !== 'true';
  }

  private toMobileBannerNotification(item: any): Notification {
    const imageUrl = item?.image_url ?? item?.avatar ?? null;
    return {
      message: {
        id: item?.id ?? 0,
        title: item?.title ?? '',
        message: typeof item?.message === 'string' ? item.message : (item?.description ?? ''),
        notification_type: item?.notification_type ?? 'info',
        read: !!item?.read,
        date: item?.created_at ?? item?.date ?? new Date().toISOString(),
        ...(imageUrl ? { image_url: imageUrl } as Record<string, unknown> : {})
      }
    } as Notification;
  }

  private clearMobileBannerPollTimer(): void {
    if (this.mobileBannerPollTimer != null) {
      clearTimeout(this.mobileBannerPollTimer);
      this.mobileBannerPollTimer = null;
    }
  }

  getRefreshRequested(): Observable<void> {
    return this.refreshRequested$.asObservable();
  }

  isWebSocketConnected(): Observable<boolean> {
    return this.wsConnected.asObservable();
  }

  /**
   * Instantly show the mobile top banner (no WebSocket / HTTP wait).
   * Also queues the item for the navbar list until the backend catches up.
   */
  public showMobileBannerImmediately(notification: {
    title: string;
    message: string;
    notification_type?: string;
  }): void {
    this.addPendingNotification({
      title: notification.title,
      message: notification.message,
      notification_type: notification.notification_type ?? 'info',
      read: false
    });
  }

  /**
   * Add a notification to show immediately (e.g. basket reopened) before/without backend.
   * It will be prepended to the list returned by getNotif() / getNotifUnreadOnly().
   */
  public addPendingNotification(notification: { title: string; message: string; notification_type: string; read: boolean; created_at?: string }): void {
    const item = {
      ...notification,
      created_at: notification.created_at || new Date().toISOString()
    };
    this.pendingNotifications.unshift(item);

    const rawNotification = this.toMobileBannerNotification(item);

    if (this.mobileToastService.isMobileViewport()) {
      console.warn(`${this.mobileBannerLogPrefix} addPendingNotification → mobile banner (instant)`, {
        title: item.title
      });
      this.mobileToastService.showFromNotification(rawNotification);
    } else {
      this.mobileToastService.logViewportStatus('addPendingNotification skipped — not mobile viewport');
    }

    this.markNotificationSeenForBanner(item);
    this.refreshRequested$.next();
  }

  /**
   * Remove one pending notification that matches the same title, message and type.
   * Use after successfully creating the same notification via createNotification() to avoid duplicates.
   */
  public removePendingNotification(match: { title: string; message: string; notification_type: string }): void {
    const idx = this.pendingNotifications.findIndex(
      p => p.notification_type === match.notification_type && p.title === match.title && p.message === match.message
    );
    if (idx !== -1) {
      this.pendingNotifications.splice(idx, 1);
    }
  }

  /**
   * Backend: GET ${apiUrl}/AD/notifications/
   * Merges pending (frontend-created) notifications with API response so they appear immediately.
   */
  public getNotif(): Observable<any> {
    return this.http.get(`${this.host}/AD/notifications/`).pipe(
      map((res: any) => {
        const rawList = Array.isArray(res) ? res : (res && res.results) ? res.results : [];
        const normalized = rawList.map((n: any) => this.normalizeNotificationRow(n));
        return [...this.pendingNotifications, ...normalized];
      }),
      catchError(() => of([...this.pendingNotifications]))
    );
  }

  /**
   * Backend: GET ${apiUrl}/AD/notifications/?unread_only=true
   * Returns only unread notifications. Merges pending unread (frontend-created) with API response.
   */
  /** Backend may send n.message as string (body) or object. Never spread a string (would become { "0":"x", "1":"y" }). */
  private normalizeNotificationRow(n: any): any {
    const isMessageObject = n.message != null && typeof n.message === 'object' && !Array.isArray(n.message);
    if (isMessageObject) {
      return { ...n.message, id: n.id ?? n.message?.id, created_at: n.message.date ?? n.created_at };
    }
    const item: any = { ...n, id: n.id ?? n.message?.id, created_at: n.created_at ?? n.date };
    if (typeof n.message === 'string') {
      item.message = n.message;
    }
    return item;
  }

  /** Normalize one notification for display (navbar popup). Ensures title and message from DB, same as tf-notification-list. */
  public normalizeForDisplay(n: any): any {
    const row = this.normalizeNotificationRow(n);
    const rawRead = row.read ?? row.is_read;
    row.read = rawRead === true || rawRead === 'true';
    row.title = row.title ?? row.subject ?? row.name ?? '';
    row.message = typeof row.message === 'string' ? row.message : (row.description ?? row.body ?? '');
    const dateValue = row.created_at ?? row.date ?? row.timestamp;
    row.created_at = dateValue;
    row.date = dateValue;
    const numKeys = Object.keys(row).filter((k: string) => /^\d+$/.test(k));
    numKeys.forEach((k: string) => delete row[k]);
    return row;
  }

  public getNotifUnreadOnly(): Observable<any> {
    return this.http.get(`${this.host}/AD/notifications/?unread_only=true`).pipe(
      map((res: any) => {
        const rawList = Array.isArray(res) ? res : (res && res.results) ? res.results : [];
        const normalized = rawList.map((n: any) => this.normalizeNotificationRow(n));
        const pendingUnread = this.pendingNotifications.filter(
          (p: any) => (p.read ?? p.is_read) !== true && (p.read ?? p.is_read) !== 'true'
        );
        return [...pendingUnread, ...normalized];
      }),
      catchError(() => {
        const pendingUnread = this.pendingNotifications.filter(
          (p: any) => (p.read ?? p.is_read) !== true && (p.read ?? p.is_read) !== 'true'
        );
        return of(pendingUnread);
      })
    );
  }

  /**
   * Backend: POST ${apiUrl}/AD/notifications/
   */
  public createNotification(payload: Partial<Notification['message']> | Record<string, unknown>): Observable<any> {
    return this.http.post(`${this.host}/AD/notifications/`, payload);
  }

  /**
   * Backend: PUT ${apiUrl}/AD/mark_read/${notificationId}/
   */
  public markAsRead(pk: number | string): Observable<any> {
    return this.http.put<any>(`${this.host}/AD/mark_read/${pk}/`, null);
  }

  /**
   * Backend: PUT ${apiUrl}/AD/mark_read/
   */
  markAllAsRead(): Observable<void> {
    return this.http.put<void>(`${this.host}/AD/mark_read/`, null);
  }

  /**
   * Backend: DELETE ${apiUrl}/AD/notifications/ (with auth headers).
   * Deletes all notifications for the current user.
   */
  deleteAllNotifications(): Observable<void> {
    return this.http.delete<void>(`${this.host}/AD/notifications/`);
  }

  /** Mark all pending (frontend-created) notifications as read so they stay in sync after mark-all. */
  markPendingNotificationsAsRead(): void {
    this.pendingNotifications.forEach(n => { n.read = true; });
  }

  /** Clear all pending (frontend-created) notifications. Use when user deletes all on the list page. */
  clearPendingNotifications(): void {
    this.pendingNotifications.length = 0;
  }

  // Restart WebSocket connection if needed
  public restartWebSocket() {
    this.clearWsReconnectTimer();
    this.wsReconnectAttempts = 0;
    this.suppressWsReconnect = true;
    this.detachAndCloseExistingSocket();
    this.suppressWsReconnect = false;
    this.initializeWebSocket();
  }
}
